import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, ChevronRight, Download, Lock } from "lucide-react";
import { studentApi, getStudentToken, setStudentToken } from "../lib/studentAuth";

const CourseLesson = () => {
    const { moduleId, lessonId } = useParams();
    const [me, setMe] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!getStudentToken()) {
            navigate("/course/login");
            return;
        }
        studentApi.get("/students/me")
            .then((r) => setMe(r.data))
            .catch(() => {
                setStudentToken(null);
                navigate("/course/login");
            });
    }, [navigate]);

    const ctx = useMemo(() => {
        if (!me?.courses?.[0]) return null;
        const course = me.courses[0];
        const moduleIdx = course.modules.findIndex((m) => m.id === moduleId);
        if (moduleIdx === -1) return null;
        const mod = course.modules[moduleIdx];
        const lessonIdx = mod.lessons.findIndex((l) => l.id === lessonId);
        if (lessonIdx === -1) return null;
        const lesson = mod.lessons[lessonIdx];
        // Build flat next/prev across module boundaries
        const flat = course.modules.flatMap((m) => m.lessons.map((l) => ({ moduleId: m.id, lessonId: l.id })));
        const flatIdx = flat.findIndex((x) => x.moduleId === moduleId && x.lessonId === lessonId);
        return {
            course, mod, lesson,
            prev: flatIdx > 0 ? flat[flatIdx - 1] : null,
            next: flatIdx < flat.length - 1 ? flat[flatIdx + 1] : null,
        };
    }, [me, moduleId, lessonId]);

    if (!me) return <div className="min-h-screen bg-black text-white/40 flex items-center justify-center font-stencil text-xs tracking-widest">LOADING...</div>;
    if (!ctx) return (
        <div className="min-h-screen bg-black text-white/60 flex items-center justify-center px-6">
            <div className="text-center">
                <h1 className="font-display text-2xl uppercase">Lesson not found.</h1>
                <Link to="/course/dashboard" className="mt-4 btn-ghost inline-flex">← Back to course</Link>
            </div>
        </div>
    );
    const { course, mod, lesson, prev, next } = ctx;

    return (
        <div className="bg-black text-white min-h-screen" data-testid="course-lesson-page">
            <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 md:py-16">
                {/* Breadcrumb */}
                <Link to="/course/dashboard" className="inline-flex items-center gap-2 text-xs font-stencil tracking-widest text-white/40 hover:text-red-600 transition-colors mb-8" data-testid="lesson-back-link">
                    <ArrowLeft size={14} /> {course.title}
                </Link>

                {/* Header */}
                <div className="mb-10">
                    <div className="font-stencil text-[10px] tracking-widest text-red-600">
                        MODULE 0{mod.number} · {mod.title.toUpperCase()} · LESSON {lesson.number}
                    </div>
                    <h1 className="mt-4 font-display text-3xl md:text-5xl uppercase tracking-tighter leading-[0.95]">
                        {lesson.title}
                    </h1>
                    <p className="mt-4 text-white/70 max-w-2xl">{lesson.description}</p>
                </div>

                {/* Video player */}
                <div className="bg-[#050505] border border-white/10 mb-8 relative overflow-hidden" style={{ paddingBottom: "56.25%" }} data-testid="lesson-video-wrapper">
                    {lesson.video_url ? (
                        <video
                            src={lesson.video_url}
                            controls
                            playsInline
                            className="absolute inset-0 w-full h-full"
                            data-testid="lesson-video-player"
                        />
                    ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                            <Lock size={36} className="text-red-600 mb-4" strokeWidth={1.5} />
                            <div className="font-stencil text-xs tracking-widest text-yellow-500/80">[ COMING SOON ]</div>
                            <div className="mt-3 font-display text-xl uppercase tracking-tight max-w-md">
                                Tim is recording this lesson.
                            </div>
                            <p className="mt-2 text-sm text-white/50 max-w-md">
                                Your access is locked in. You'll see this lesson here the moment it's ready.
                            </p>
                        </div>
                    )}
                </div>

                {/* Workbook + actions */}
                <div className="flex flex-wrap gap-3 mb-12">
                    {lesson.workbook_url ? (
                        <a
                            href={lesson.workbook_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary text-sm"
                            data-testid="lesson-workbook-link"
                        >
                            <BookOpen size={16} /> Download workbook PDF
                        </a>
                    ) : (
                        <span className="btn-ghost text-sm opacity-50 cursor-not-allowed">
                            <BookOpen size={16} /> Workbook coming soon
                        </span>
                    )}
                </div>

                {/* Lesson infographic / supplemental image */}
                {lesson.image_url && (
                    <div className="mb-12" data-testid="lesson-image-wrapper">
                        <div className="font-stencil text-[10px] tracking-widest text-red-600 mb-3">
                            [ INFOGRAPHIC ]
                        </div>
                        <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${lesson.image_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block border border-white/10 bg-[#050505] overflow-hidden hover:border-red-600/60 transition-colors"
                            data-testid="lesson-image-link"
                        >
                            <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${lesson.image_url}`}
                                alt={lesson.image_alt || lesson.title}
                                className="w-full h-auto block"
                                loading="lazy"
                                data-testid="lesson-image"
                            />
                        </a>
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                            <a
                                href={`${process.env.REACT_APP_BACKEND_URL}${lesson.image_url}?download=1`}
                                className="btn-ghost text-xs"
                                data-testid="lesson-image-download"
                            >
                                <Download size={14} /> Save image
                            </a>
                            <span className="text-xs text-white/40">Or tap the image to open full-size.</span>
                        </div>
                    </div>
                )}

                {/* Prev / Next */}
                <div className="border-t border-white/10 pt-8 grid grid-cols-2 gap-4">
                    {prev ? (
                        <Link to={`/course/lesson/${prev.moduleId}/${prev.lessonId}`} className="btn-ghost text-xs justify-self-start" data-testid="lesson-prev">
                            <ArrowLeft size={14} /> Previous
                        </Link>
                    ) : <span />}
                    {next ? (
                        <Link to={`/course/lesson/${next.moduleId}/${next.lessonId}`} className="btn-primary text-xs justify-self-end" data-testid="lesson-next">
                            Next lesson <ChevronRight size={14} />
                        </Link>
                    ) : <span />}
                </div>
            </div>
        </div>
    );
};

export default CourseLesson;
